/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, JSX } from 'preact';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { useCallback, useImperativeHandle, useRef } from 'preact/hooks';
import { ReorderableContext } from '../hooks/PRIVATE_useReorderable';
import { DataState, CurrentKeyDetail, ListItemRendererContext } from '../UNSAFE_Collection';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { List, SkeletonContainer } from '../PRIVATE_List';
import {
  ListProps,
  ListViewContextMenuConfig,
  ListViewHandle,
  SkeletonRendererContext
} from './List.types';
import { getScrollPosition } from './ScrollPositionUtils';
import { MemoizeListViewReorderableItem } from './ListViewReorderableItem';
import { useListViewReorder } from './useListViewReorder';
import { ListViewContextMenu } from './ListViewContextMenu';
import { useListViewContextMenu } from './useListViewContextMenu';
import { useListViewCurrentItemOverride } from './useListViewCurrentItemOverride';
import { LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';

/**
 * Props specific to non-virtualized ListView
 */
type NonVirtualizedListProps<K, D> = {
  /**
   * The data that is used to render each item in ListView.  If the value is null, then ListView will show nothing
   * if hasMore is false, or loading indicator if hasMore is true.
   */
  data: D[] | null;
  /**
   * A callback function to extract the key which is used to identify the item.
   */
  getRowKey: (data: D) => K;
  /**
   * Whether there are more data available to load.  By default this returns false.
   */
  hasMore?: boolean;
  /**
   * An optional callback function which is invoked when user scrolls to the end of the list.
   */
  onLoadMore?: () => void;

  /**
   * Configuration used to specify a context menu.
   */
  contextMenuConfig?: ListViewContextMenuConfig<K, D>;
};

/**
 * Props for the ListView Component
 */
export type Props<K, D> = Pick<
  ListProps<K, D>,
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'children'
  | 'gridlines'
  | 'currentItemOverride'
  | 'onPersistCurrentItem'
  | 'onItemAction'
  | 'onSelectionChange'
  | 'promotedSection'
  | 'selectedKeys'
  | 'selectionMode'
  | 'viewportConfig'
  | 'testId'
  | 'scrollPositionOverride'
  | 'onReorder'
  | 'itemPadding'
  | 'itemEnterKeyFocusBehavior'
  | 'skeletonRenderer'
> &
  NonVirtualizedListProps<K, D>;
/**
 * A list view displays data items as a list or a grid with highly interactive features.
 */
export const ListView = forwardRef(
  <K extends string | number, D>(
    { children, ...props }: Props<K, D>,
    ref?: ForwardedRef<ListViewHandle<K>>
  ) => {
    const { contextMenuConfig } = props;
    const listRef = useRef<HTMLDivElement | null>(null);

    const { currentItemOverride, updateCurrentItemOverride } = useListViewCurrentItemOverride(
      props.currentItemOverride
    );

    const dataMetadata = props.data
      ? props.data.map((aData: D) => {
          const key = props.getRowKey(aData);
          return { data: aData, metadata: { key } };
        })
      : null;

    const loadRange = props.onLoadMore ? props.onLoadMore : () => {};
    const dataState = dataMetadata
      ? ({
          offset: 0,
          data: dataMetadata,
          sizePrecision: props.hasMore ? 'atLeast' : 'exact',
          totalSize: dataMetadata.length
        } as DataState<K, D>)
      : null;

    useImperativeHandle(ref!, () => ({
      getScrollPosition: () => {
        if (listRef.current) {
          return getScrollPosition(listRef.current);
        }
        return { y: 0 };
      },
      focus: () => {
        if (listRef.current) {
          listRef.current.focus();
        }
      }
    }));

    /**
     * Reorder Section
     */
    const isReorderEnabled = props.onReorder != undefined;
    const {
      reorderProps,
      reorderItemProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } = useListViewReorder({
      onReorder: props.onReorder,
      rootRef: listRef,
      viewportConfig: props.viewportConfig
    });

    const { triggerProps, menuProps, contextMenuContext, notifyCurrentKeyChanged } =
      useListViewContextMenu(dataState, contextMenuConfig, listRef, updateCurrentItemOverride);

    const onPersistCurrentItem = (detail: CurrentKeyDetail<K>) => {
      props.onPersistCurrentItem?.(detail);
      notifyCurrentKeyChanged(detail);
    };

    const reorderableItemRenderer = useCallback(
      (context: ListItemRendererContext<K, D>) => (
        <MemoizeListViewReorderableItem {...reorderItemProps} itemKey={context.metadata.key}>
          {children(context)}
        </MemoizeListViewReorderableItem>
      ),
      [children, reorderItemProps]
    );

    if (isReorderEnabled && reorderItemProps) {
      return (
        <>
          <ReorderableContext.Provider value={reorderContext}>
            <List
              {...props}
              aria-describedby={reorderInstructionsId}
              contextMenuTriggerProps={triggerProps}
              reorderProps={reorderProps}
              onLoadRange={loadRange}
              scrollerRef={listRef}
              data={dataState}
              currentItemOverride={currentItemOverride}
              onPersistCurrentItem={onPersistCurrentItem}>
              {reorderableItemRenderer}
            </List>
            <span id={reorderInstructionsId}>
              <HiddenAccessible>{reorderInstructions}</HiddenAccessible>
            </span>
          </ReorderableContext.Provider>
          {contextMenuConfig && (
            <ListViewContextMenu
              {...menuProps}
              testId={props.testId ? props.testId + '-menu' : undefined}
              contextMenuConfig={contextMenuConfig}
              contextMenuContext={contextMenuContext}
            />
          )}
        </>
      );
    }

    const customInitialSkeleton = (
      skeletonRenderer: (context: SkeletonRendererContext) => ComponentChildren
    ) => {
      return (
        <SkeletonContainer minimumCount={25}>
          {(index) => {
            const context: SkeletonRendererContext = {
              loadingStatus: 'initial',
              index: index
            };
            return skeletonRenderer(context);
          }}
        </SkeletonContainer>
      );
    };

    const customLoadMoreSkeleton = (
      skeletonRenderer: (context: SkeletonRendererContext) => ComponentChildren
    ) => {
      return (
        <div class={LOADMORE_STYLE_CLASS}>
          <SkeletonContainer minimumCount={3}>
            {(index) => {
              const context: SkeletonRendererContext = {
                loadingStatus: 'loadMore',
                index: index
              };
              return skeletonRenderer(context);
            }}
          </SkeletonContainer>
        </div>
      );
    };

    return (
      <>
        <List
          {...props}
          loadingIndicator={
            props.skeletonRenderer ? customInitialSkeleton(props.skeletonRenderer) : undefined
          }
          loadMoreIndicator={
            props.skeletonRenderer ? customLoadMoreSkeleton(props.skeletonRenderer) : undefined
          }
          onLoadRange={loadRange}
          scrollerRef={listRef}
          data={dataState}
          contextMenuTriggerProps={triggerProps}
          currentItemOverride={currentItemOverride}
          onPersistCurrentItem={onPersistCurrentItem}>
          {children}
        </List>
        {contextMenuConfig && (
          <ListViewContextMenu
            {...menuProps}
            contextMenuConfig={contextMenuConfig}
            contextMenuContext={contextMenuContext}
            testId={props.testId ? props.testId + '-menu' : undefined}
          />
        )}
      </>
    );
  }
) as <K extends string | number, D>(
  props: Props<K, D> & { ref?: ForwardedRef<ListViewHandle<K>> }
) => JSX.Element;
